import App from '@frontend/application-container';
import { withSpinner } from '@frontend/state/spinner';
import type { Edge, Face, Vertex } from '@phi/jcore';
import { get_core_side_dragging_state } from '@frontend/JCore/jcore';
import { handle_mouseup_drag_end_events } from '@frontend/pointer/mouse';
import {
    uil_get_edges_of_face,
    uil_get_faces_of_edge,
    uil_get_point_at_edge_param,
    uil_get_tangent_at_edge_param,
    uil_get_vertices_of_edge,
    uil_is_object_visible,
    uil_listify_edges,
    uil_make_faces_invisible,
    uil_make_faces_visible,
    uil_pop_faces,
} from '@frontend/ui_logic';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { WrappedValue } from '@phi/wrapped-value';
import {
    properly_split_invisible_face_connected_components,
    uid_deselect_all_objects,
    uid_hide_rigid_move_manipulator,
    uid_pop,
    uid_pop_many,
    uid_select_object,
    uid_show_rigid_move_manipulator,
} from '@frontend/ui_display';
import { push_nodups,
    suppress_animate,
    suppress_core_face_tessellation,
    suppress_undo_tracking,
    unsuppress_animate_if_ready,
    unsuppress_core_face_tessellation_if_ready,
    unsuppress_undo_tracking_if_ready,
} from '@frontend/utility';
import { Vector3 } from 'three';
import { angle } from '@frontend/vector3';

export const uid_popout_one_or_many = (x: KeyboardEvent | null = null) => uid_prepare_to_pop(x, true);

export const uid_popin_one_or_many = (x: KeyboardEvent | null = null) => uid_prepare_to_pop(x, false);

export const uid_prepare_to_pop = withSpinner('Extruding', (event: KeyboardEvent | MouseEvent | null, is_popout: boolean) => {
    if (
        uid_raynode === uid_objects_raynode
        && !App.state.drawing_edge
        && !App.state.carving
        && (uid_selected_faces.length > 0 || uid_selected_edges.length > 0)
    ) {
        uid_pop_one_or_many(is_popout);
    } else if (
        uid_raynode === uid_manip_raynode
        && get_core_side_dragging_state() === 'off'
    ) {
        if (event instanceof MouseEvent)
            handle_mouseup_drag_end_events(event);
        uid_pop_one_or_many(is_popout);
    }
}, () => uid_selected_faces.length > 3);

function uid_pop_one_or_many(is_popout: boolean) {
    // Does the popping:
    // - For faces in or out, one or many
    // - For edges only out, one or many

    if (uid_selected_faces.length === 1)
        uid_pop(is_popout, false);
    else if (uid_selected_faces.length > 1)
        uid_pop_many(is_popout, false);
    else if (uid_selected_edges.length > 0) {
        let all_edges_ok = true;
        uid_selected_edges.keys().forEach((eid) => {
            const fs = uil_get_faces_of_edge(eid);
            if (
                (uil_is_object_visible(fs[0]) && uil_is_object_visible(fs[1]))
                || (!uil_is_object_visible(fs[0]) && !uil_is_object_visible(fs[1]))
            )
                all_edges_ok = false;
        });
        if (!all_edges_ok) {
            display_message_to_user(LOG.WARN, 'You can only pop-out border edges');
            return;
        } else if (uid_selected_edges.length === 1) {
            uid_popout_border_edges(uid_selected_edges.keys());
        } else {
            // More than one edges, make sure they're all connected
            const wv_is_open_list = new WrappedValue(0);
            const selected_edges = [... uid_selected_edges.keys()];
            const sorted_edges: Edge[] = [];

            const res = uil_listify_edges(selected_edges, wv_is_open_list, sorted_edges);
            if (res)
                uid_popout_border_edges(uid_selected_edges.keys());
            else {
                display_message_to_user(LOG.WARN, 'You can only pop-out a chain of connected border edges');
                return;
            }
        }
    }
}

export function uid_popout_border_edges(edge_ids: Edge[]) {
    // We're simulating a pop-out of a set of border edges (edges that are all connected and where each has a visible face on one side
    // and an invisible one on the other.

    // Hide rigid move manipulator if it was visible.
    uid_hide_rigid_move_manipulator(false, 0, true);

    uid_deselect_all_objects();

    suppress_undo_tracking();
    suppress_animate();
    suppress_core_face_tessellation();

    // 0. Make sure invisible stuff is in good shape
    // Make sure that the vertices of all edges in edge_ids are used to split
    const vertices_to_definitely_split: Vertex[] = [];
    edge_ids.forEach((eid) => {
        push_nodups(uil_get_vertices_of_edge(eid)[0], vertices_to_definitely_split);
        push_nodups(uil_get_vertices_of_edge(eid)[1], vertices_to_definitely_split);
    });

    properly_split_invisible_face_connected_components(vertices_to_definitely_split, false, false);

    // 1. Take note of all currently visible faces
    const visible_faces = uid_faces.keys()
        .filter(uil_is_object_visible);

    // 2. Collect all invisible faces that border on one of the edges in edge_ids
    const faces_to_pop: Face[] = [];
    const faces_for_direction: Face[] = [];
    const vertices_for_direction: Vertex[] = [];
    const edges_for_direction: Edge[] = [];
    const average_direction_vector = new Vector3();

    edge_ids.forEach((eid) => {
        if (!uil_is_object_visible(uil_get_faces_of_edge(eid)[0]))
            push_nodups(uil_get_faces_of_edge(eid)[0], faces_to_pop);
        else
            push_nodups(uil_get_faces_of_edge(eid)[0], faces_for_direction);

        if (!uil_is_object_visible(uil_get_faces_of_edge(eid)[1]))
            push_nodups(uil_get_faces_of_edge(eid)[1], faces_to_pop);
        else
            push_nodups(uil_get_faces_of_edge(eid)[1], faces_for_direction);

        push_nodups(uil_get_vertices_of_edge(eid)[0], vertices_for_direction);
        push_nodups(uil_get_vertices_of_edge(eid)[1], vertices_for_direction);
    });

    faces_for_direction.forEach((fid) => {
        uil_get_edges_of_face(fid)
            .filter(eid =>
                !edges_for_direction.includes(eid)
                && uil_is_object_visible(eid)
            )
            .forEach((eid) => {
                let vid: Vertex | 0 = 0;
                if (
                    vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[0])
                    && !vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[1])
                )
                    vid = uil_get_vertices_of_edge(eid)[0];
                else if (
                    !vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[0])
                    && vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[1])
                )
                    vid = uil_get_vertices_of_edge(eid)[1];

                if (vid === 0)
                    return;

                push_nodups(eid, edges_for_direction);

                const p0 = uil_get_point_at_edge_param(eid, 0);
                const p1 = uil_get_point_at_edge_param(eid, 1);
                let t: Vector3;
                if (p0.distanceTo(uid_vertices.getItem(vid)!.object3d!.position) < p1.distanceTo(uid_vertices.getItem(vid)!.object3d!.position)) {
                    t = uil_get_tangent_at_edge_param(eid, 0);
                    const p0a = uil_get_point_at_edge_param(eid, 0.1);
                    const v = new Vector3();
                    v.subVectors(p0, p0a).normalize();
                    if (angle(t, v) > (Math.PI / 2.0))
                        t.multiplyScalar(-1);
                } else {
                    t = uil_get_tangent_at_edge_param(eid, 1);
                    const p1a = uil_get_point_at_edge_param(eid, 0.9);
                    const v = new Vector3();
                    v.subVectors(p1, p1a).normalize();
                    if (angle(t, v) > (Math.PI / 2.0))
                        t.multiplyScalar(-1);
                }

                average_direction_vector.add(t);
            });
    });

    average_direction_vector.normalize();

    // 4. Pop these faces after making them visible
    uil_make_faces_visible(faces_to_pop);

    // Pop out along the average direction vector
    const pop_vector = uil_pop_faces(faces_to_pop, false, average_direction_vector, 0);
    if (!pop_vector) {
        display_message_to_user(LOG.WARN, 'Failed to pop-out border edge');
        return;
    }

    // Make again invisible everything except the faces that were visible before and the faces that are attached to edge_ids
    const faces_for_edge_move_manipulator: Face[] = [];
    edge_ids
        .forEach((eid) => {
            if (visible_faces.includes(uil_get_faces_of_edge(eid)[0]))
                push_nodups(uil_get_faces_of_edge(eid)[1], faces_for_edge_move_manipulator);
            else if (visible_faces.includes(uil_get_faces_of_edge(eid)[1]))
                push_nodups(uil_get_faces_of_edge(eid)[0], faces_for_edge_move_manipulator);
        });

    const edges_for_move_manipulator: Edge[] = [];
    faces_for_edge_move_manipulator.forEach((fid) => {
        const es = uil_get_edges_of_face(fid);
        es.forEach((eid) => {
            if (
                !vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[0])
                && !vertices_for_direction.includes(uil_get_vertices_of_edge(eid)[1])
            )
                push_nodups(eid, edges_for_move_manipulator);
        });
    });

    edge_ids.forEach((eid) => {
        push_nodups(uil_get_faces_of_edge(eid)[0], visible_faces);
        push_nodups(uil_get_faces_of_edge(eid)[1], visible_faces);
    });

    const faces_to_make_visible: Face[] = [];
    const faces_to_make_invisible: Face[] = [];
    uid_faces.keys().forEach(fid => {
        if (!uil_is_object_visible(fid) && visible_faces.includes(fid))
            faces_to_make_visible.push(fid);
        if (uil_is_object_visible(fid) && !visible_faces.includes(fid))
            faces_to_make_invisible.push(fid);
    });
    uil_make_faces_visible(faces_to_make_visible);
    uil_make_faces_invisible(faces_to_make_invisible);

    uid_deselect_all_objects();

    // Make sure once again that invisible stuff is in good shape
    properly_split_invisible_face_connected_components([], false, false);

    // Attach move manipulator to popped edges
    edges_for_move_manipulator.forEach((eid) => {
        uid_select_object(eid);
    });

    unsuppress_core_face_tessellation_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();

    uid_show_rigid_move_manipulator(false, 0, true, false, false, false, false, false);
}
